// Global Instructions Rule Applied!
import { useMemo, useCallback } from 'react'
import { useSelector } from 'react-redux'
import {
  PipelineStageEntry,
  validatePipelineStageEntry
} from '../../../state_manager/data_models/pipeline_stage_entry.ts'
import {
  FIELD_KEY_PROG,
  PIPELINE_TYPE_DEAL_ORIGINATION,
  VIEW_TYPE_KANBAN_BOARD,
  VIEW_TYPE_TABLE
} from '../../../state_manager/globals.ts'
import { PipelineStagesModelFields } from '../../../state_manager/data_models/pipeline_stages_model.ts'
import type { RootState } from '../../../state_manager/store.ts'

/**
 * Custom hook for managing deal-related pipeline operations
 * Provides access to default pipeline ID and pipeline stages filtering
 */
export function useDealManager() {
  // Redux selectors
  const pipelines = useSelector((state: RootState) => state.globalConfigs?.pipelines || [])
  const pipelineStages = useSelector(
    (state: { globalConfigs?: { pipeline_stages?: unknown[] } }) => state.globalConfigs?.pipeline_stages || []
  )
  const pipelineConfigs = useSelector(
    (state: { globalConfigs?: { pipeline_configs?: unknown[] } }) => state.globalConfigs?.pipeline_configs || []
  )

  // Get the default Deal Origination pipeline ID
  const defaultPipelineId = useMemo((): string | null => {
    const defaultPipeline = pipelines.find(
      (pipeline) => pipeline.default === true && pipeline.type === PIPELINE_TYPE_DEAL_ORIGINATION
    )

    return defaultPipeline?.id || null
  }, [pipelines])

  // Get pipeline stages for a specific pipeline and view type
  const getPipelineStages = useCallback(
    (pipelineId: string, viewType: typeof VIEW_TYPE_KANBAN_BOARD | typeof VIEW_TYPE_TABLE): PipelineStageEntry[] => {
      if (!pipelineId) return []

      // Get all pipeline configs for the pipeline
      const filteredConfigs = (pipelineConfigs as Array<{ pipeline?: string; [key: string]: unknown }>).filter(
        (config) => config.pipeline === pipelineId && config[`visible_${viewType}`] === true
      )

      // Get all pipeline stages for the pipeline based on the pipeline configs
      const filteredStages = (
        pipelineStages as Array<{ id?: string; name?: string | null; decision_status?: string | null }>
      ).filter((stage) => filteredConfigs.some((config) => config.stage === stage.id))

      // Combine the pipeline stages and pipeline configs into a single array of PipelineStageEntry
      const result: PipelineStageEntry[] = []

      // Loop through the filtered configs and add pipeline stage properties to the result array
      for (const config of filteredConfigs) {
        const stage = filteredStages.find(
          (stage: { id?: string; name?: string | null; decision_status?: string | null }) => stage.id === config.stage
        )
        if (stage) {
          result.push({
            ...config,
            [`${FIELD_KEY_PROG}${PipelineStagesModelFields.name}`]: stage.name,
            [`${FIELD_KEY_PROG}${PipelineStagesModelFields.decision_status}`]: stage.decision_status
          } as PipelineStageEntry)
        }
      }

      // Ordering is the consumer's: both kanbans set each column's `sequence`
      // from `sequence_kb` and KanbanBoard sorts by it. (A sort by a
      // non-existent `sequence` key used to sit here and never reordered.)
      const validatedData = validatePipelineStageEntry(result, { isArray: true }) as PipelineStageEntry[]

      return validatedData
    },
    [pipelineConfigs, pipelineStages]
  )

  return {
    defaultPipelineId,
    getPipelineStages
  }
}
